import React, { useEffect, useMemo, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CButton,
  CFormInput,
  CFormLabel,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CContainer,
} from "@coreui/react";
import { CIcon } from "@coreui/icons-react";
import {
  cilCalendar,
  cilInfo,
  cilEnvelopeOpen,
  cilWarning,
} from "@coreui/icons";
import { apiClient } from "services/Api";
import LoadingScreen from "components/globals/LoadingScreen";
import ErrorPage from "components/globals/ErrorPage";

const MONTHS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const formatDays = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || num === 0) return "";
  const fixed = num.toFixed(1);
  return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
};

const getInitials = (firstName, lastName) => {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();
  if (!first && !last) return "?";
  const firstChar = first ? first[0] : "";
  const lastChar = last ? last[0] : "";
  return `${firstChar}${lastChar}`.toUpperCase();
};

const Holidays = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [draftYear, setDraftYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);

    apiClient
      .get(`internal-management/holidays/?year=${year}`)
      .then((response) => {
        setData(response.data || {});
      })
      .catch(() => {
        setError(true);
      });
  }, [year]);

  const rows = useMemo(() => {
    if (!data) return [];
    return (data.users || []).sort((a, b) => {
      const nameA = `${a.first_name || ""} ${a.last_name || ""}`.trim();
      const nameB = `${b.first_name || ""} ${b.last_name || ""}`.trim();
      return nameA.localeCompare(nameB, "pt");
    });
  }, [data]);

  const getPreviousMonths = (user) => {
    return user.months_last_year || {};
  };

  const getPreviousTotal = (user, prevMonths) => {
    if (Number.isFinite(Number(user.total_last_year))) {
      return Number(user.total_last_year);
    }
    return Object.values(prevMonths || {}).reduce(
      (acc, value) => acc + Number(value || 0),
      0,
    );
  };

  if (error) {
    return <ErrorPage message="Erro ao carregar dados de férias." />;
  }

  if (!data) {
    return <LoadingScreen message="A carregar férias..." />;
  }

  const emailTemplate =
    "Exmo.(a) Senhor(a),\n\n" +
    "Encontro-me ausente até dia/mês.\n" +
    "Peço que reenvie o email para o colaborador(a) Nome Apelido (nome.apelido@streamconsulting.pt).\n\n" +
    "Pode também, se entender, contactar diretamente o escritório através do número 244 836 535.\n\n" +
    "Melhores Cumprimentos,\n" +
    "Nome Apelido";

  return (
    <CContainer>
      <div className="management-filters mb-4">
        <div className="management-filters-header">
          <div className="management-section-title">Filtros</div>
          <CButton color="primary" size="sm" onClick={() => setYear(draftYear)}>
            Aplicar filtros
          </CButton>
        </div>
        <CRow className="g-3 management-filters-grid">
          <CCol xs={12} md={4} lg={2}>
            <CFormLabel>Ano de referência</CFormLabel>
            <CFormInput
              id="holidays-year"
              type="number"
              min={2000}
              max={now.getFullYear() + 1}
              value={draftYear}
              onChange={(event) => {
                const nextYear = Number(event.target.value);
                if (Number.isFinite(nextYear) && nextYear > 0) {
                  setDraftYear(nextYear);
                }
              }}
            />
          </CCol>
        </CRow>
      </div>

      <CCard>
        <CCardHeader>Férias - Ano de {year}</CCardHeader>
        <CCardBody>
          <CTable bordered responsive hover className="holidays-table mb-1">
            <CTableHead color="light">
              <CTableRow>
                <CTableHeaderCell scope="col">Colaborador</CTableHeaderCell>
                {MONTHS.map((month) => (
                  <CTableHeaderCell
                    key={month}
                    scope="col"
                    className="text-center"
                  >
                    {month}
                  </CTableHeaderCell>
                ))}
                <CTableHeaderCell scope="col" className="text-center">
                  Total
                </CTableHeaderCell>
                <CTableHeaderCell scope="col" className="text-center">
                  Remanescente
                </CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((user) => {
                const fullName = `${user.first_name || ""} ${
                  user.last_name || ""
                }`.trim();
                const prevMonths = getPreviousMonths(user);
                const prevTotal = getPreviousTotal(user, prevMonths);
                const initials = getInitials(user.first_name, user.last_name);
                const entitlement = Number(user.entitlement || 0);
                const total = Number(user.total || 0);
                const transition = entitlement - total;
                const remaining_last_year = user.remaining_last_year;
                const transition_last_year =
                  remaining_last_year - user.total_last_year;
                const transitionValue = Number.isFinite(transition)
                  ? transition
                  : null;
                const isNegative =
                  transitionValue !== null && transitionValue < 0;

                return (
                  <CTableRow key={user.teamwork_id}>
                    <CTableDataCell style={{ minWidth: 220 }}>
                      <div className="holidays-user">
                        {user.avatar_url ? (
                          <img
                            src={user.avatar_url}
                            alt={fullName}
                            className="holidays-user-avatar"
                          />
                        ) : (
                          <span className="holidays-user-avatar holidays-user-avatar--fallback">
                            {initials}
                          </span>
                        )}
                        <div className="holidays-user-info">
                          <div className="holidays-user-name">
                            {fullName || `#${user.teamwork_id}`}
                          </div>
                          <div className="holidays-user-entitlement">
                            Direito anual: {formatDays(user.entitlement)} dias
                          </div>
                        </div>
                      </div>
                    </CTableDataCell>
                    {MONTHS.map((_, index) => {
                      const monthKey = index + 1;
                      const currentValue = formatDays(user.months?.[monthKey]);
                      const previousValue = formatDays(prevMonths?.[monthKey]);
                      const displayCurrent =
                        currentValue || (previousValue ? "0" : "-");
                      return (
                        <CTableDataCell
                          key={`${user.teamwork_id}-${monthKey}`}
                          className="text-center"
                        >
                          {displayCurrent ? (
                            <span className="holidays-value holidays-value--current">
                              {displayCurrent}
                            </span>
                          ) : null}
                          {previousValue ? (
                            <span className="holidays-value holidays-value--prev">
                              ({previousValue})
                            </span>
                          ) : null}
                        </CTableDataCell>
                      );
                    })}
                    <CTableDataCell className="text-center">
                      {(() => {
                        const currentTotalValue = formatDays(user.total);
                        const prevTotalValue = formatDays(prevTotal);
                        const displayTotalCurrent =
                          currentTotalValue || (prevTotalValue ? "0" : "-");
                        return (
                          <>
                            {displayTotalCurrent ? (
                              <span className="holidays-value holidays-value--current">
                                {displayTotalCurrent}
                              </span>
                            ) : null}
                            {prevTotalValue ? (
                              <span className="holidays-value holidays-value--prev">
                                ({prevTotalValue})
                              </span>
                            ) : null}
                          </>
                        );
                      })()}
                    </CTableDataCell>
                    <CTableDataCell className="text-center">
                      {transitionValue === null ? (
                        <span className="holidays-value holidays-value--current">
                          -
                        </span>
                      ) : (
                        <>
                          <span
                            className={`holidays-value holidays-value--current holidays-transition${
                              isNegative ? " holidays-transition--negative" : ""
                            }`}
                          >
                            {formatDays(transitionValue)}
                          </span>
                          {remaining_last_year || prevTotal ? (
                            <span className="holidays-value holidays-value--prev">
                              ({formatDays(transition_last_year) || 0})
                            </span>
                          ) : null}
                          {isNegative ? (
                            <span className="holidays-transition-warning">
                              <CIcon icon={cilWarning} title="Ultrapassado" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                );
              })}
            </CTableBody>
          </CTable>

          <div className="holidays-table-legend text-muted">
            <strong>Legenda:</strong> Valores entre parênteses correspondem a
            dias de férias do ano anterior.
          </div>
        </CCardBody>
      </CCard>

      <CRow className="gy-3 mt-4 holidays-guidelines">
        <CCol lg={4}>
          <CCard className="holidays-info-card holidays-info-card--primary h-100">
            <CCardBody>
              <div className="d-flex align-items-center gap-3 mb-2">
                <div className="holidays-info-icon">
                  <CIcon icon={cilCalendar} />
                </div>
                <div className="holidays-info-title">Procedimento Interno</div>
              </div>
              <p>
                Anualmente, o colaborador tem de marcar os dias de férias que
                tem direito, através da plataforma Teamwork.
              </p>
              <p>
                A designação do evento deverá ser:
                <br />
                <strong>FÉRIAS AAAA | Nome Apelido</strong> – usar para os dias
                que o colaborador pretende usufruir.
              </p>
              <p className="mb-0">
                <strong>Notas:</strong>
                <ul>
                  <li>
                    O "AAAA" deve ser substituído pelo ano a que as férias dizem
                    respeito.
                  </li>
                  <li>
                    O colaborador deve notificar sempre a gerência e o gestor da
                    sua respetiva área, através da opção Notify.
                  </li>
                  <li>
                    São consideradas meias jornadas de férias os eventos que não
                    abrangem o dia completo.
                  </li>
                </ul>
              </p>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={4}>
          <CCard className="holidays-info-card holidays-info-card--secondary h-100">
            <CCardBody>
              <div className="d-flex align-items-center gap-3 mb-2">
                <div className="holidays-info-icon">
                  <CIcon icon={cilInfo} />
                </div>
                <div className="holidays-info-title">Enquadramento Legal</div>
              </div>
              <p>
                As férias serão gozadas, anualmente, evitando determinados
                períodos de maior trabalho, previamente designados pela STREAM
                Consulting.
              </p>
              <p>
                No ano da admissão, o colaborador tem direito, após 6 meses de
                prestação de trabalho, a gozar 2 dias úteis por cada mês de
                duração do contrato, no máximo de 20 dias úteis. Consideram-se
                dias úteis, os dias de semana de 2.ª a 6.ª feira, com exceção
                dos feriados.
              </p>
              <p>
                Nos restantes anos, regra geral, o período anual de férias tem a
                duração de 22 dias úteis.
              </p>
              <p className="mb-0">
                Para colaboradores com mais de 10 anos de serviço na empresa, o
                período anual de férias tem a duração de 23 dias úteis.
              </p>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={4}>
          <CCard className="holidays-info-card holidays-info-card--accent h-100">
            <CCardBody>
              <div className="d-flex align-items-center gap-3 mb-2">
                <div className="holidays-info-icon">
                  <CIcon icon={cilEnvelopeOpen} />
                </div>
                <div className="holidays-info-title">Resposta Automática</div>
              </div>
              <p>
                Sempre que a ausência seja superior a 2 dias (ex.: férias,
                baixa, etc.), deve ser criada uma mensagem automática para o
                remetente.
              </p>
              <p>
                Na fase de agendamento das férias, o colaborador deve adicionar
                um Reminder para um dia antes do período de férias com a
                seguinte informação: Criar mensagem de ausência automática para
                o remetente.
              </p>
              <div className="holidays-info-message">
                <div className="holidays-info-message-header">
                  <span>Mensagem Automática</span>
                  <CButton
                    size="sm"
                    color="primary"
                    variant="ghost"
                    onClick={() => navigator.clipboard.writeText(emailTemplate)}
                  >
                    Copiar texto
                  </CButton>
                </div>
                <pre className="holidays-info-message-body">
                  {emailTemplate}
                </pre>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  );
};

export default Holidays;
